import classNames from 'classnames';

import { Combobox, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';

import { PeopleApi } from '../../api';
import type { Patient } from '../../model';

const DEBOUNCE_MS = 300;

const PLACEHOLDER_SURNAME = 'Клиент';

const getPatientComboboxLabel = (patient: Patient): string => {
  const phone = patient.phone?.trim();
  const hasGivenOrMid = Boolean(
    patient.name?.trim() || patient.midname?.trim(),
  );
  const rawSurname = patient.surname?.trim() ?? '';
  const isPlaceholderOnly =
    rawSurname === PLACEHOLDER_SURNAME && !hasGivenOrMid;
  if (!isPlaceholderOnly) {
    const fromName = patient.fullName.trim();
    if (fromName.length > 0) {
      return fromName;
    }
  }
  if (phone) {
    return phone;
  }
  const fallback = patient.fullName.trim();
  if (fallback.length > 0) {
    return fallback;
  }
  return `Пациент №${patient.id}`;
};

type Props = {
  className?: string;
  placeholder?: string;
  error?: FieldError;
  onChange(newValue: number): void;
  value: number;
  outlineAttention?: boolean;
};

export const PatientAutocomplete: FC<Props> = ({
  className,
  error,
  onChange,
  outlineAttention,
  value,
  placeholder,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const selectedNameRef = useRef<string>('');
  const [showOutlineAttention, setShowOutlineAttention] = useState(
    Boolean(outlineAttention),
  );

  useEffect(() => {
    if (!outlineAttention) return;
    const timer = window.setTimeout(() => setShowOutlineAttention(false), 2200);
    return () => window.clearTimeout(timer);
  }, [outlineAttention]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [query]);

  const {
    data: patientsData = { total: 0, items: [] },
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: [PeopleApi.KEY, 'getPatients', debouncedQuery],
    queryFn: () => PeopleApi.getPatients(debouncedQuery),
  });
  const patients = patientsData.items;

  const { data: selectedPatientDetails } = useQuery({
    queryKey: [PeopleApi.KEY, 'getPatientDetails', value],
    queryFn: () => PeopleApi.getPatientDetails(String(value)),
    enabled: !!value && value !== 0,
  });

  useEffect(() => {
    if (value && value !== 0) {
      const found = patients.find((p) => p.id === value);
      if (found) {
        selectedNameRef.current = getPatientComboboxLabel(found);
      } else if (selectedPatientDetails) {
        selectedNameRef.current = getPatientComboboxLabel(
          selectedPatientDetails,
        );
      }
      setQuery('');
    } else {
      selectedNameRef.current = '';
    }
  }, [value, patients, selectedPatientDetails]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setQuery('');
      onChange(0);
    },
    [onChange],
  );

  const showClear = query !== '' || (value !== 0 && value != null);

  const selectedFromList =
    value && value !== 0 ? patients.find((p) => p.id === value) : undefined;

  const displayValue =
    value && value !== 0
      ? selectedFromList
        ? getPatientComboboxLabel(selectedFromList)
        : selectedPatientDetails
        ? getPatientComboboxLabel(selectedPatientDetails)
        : selectedNameRef.current
      : query;

  return (
    <Combobox value={value || null} onChange={onChange}>
      <div
        className={classNames(
          'relative rounded-[12px] transition-shadow',
          className,
          {
            'ring-2 ring-[#0788f1] ring-offset-2 ring-offset-white':
              showOutlineAttention,
          },
        )}
      >
        <Combobox.Input
          className={classNames(
            'input input--block bg-white h-[60px] text-left pr-[90px]',
            {
              'input--invalid': Boolean(error),
            },
          )}
          displayValue={() => displayValue}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder || 'Выбор пациента'}
        />
        {showClear && (
          <button
            type="button"
            className="absolute right-[12px] top-1/2 flex size-[40px] -translate-y-1/2 items-center justify-center rounded-full text-brand-1/60 hover:bg-brand-2 hover:text-brand-1"
            onClick={handleClear}
            aria-label="Очистить"
          >
            <svg
              className="size-[20px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Combobox.Options className="absolute z-50 mt-[10px] max-h-[300px] w-full overflow-y-auto rounded-[12px] border border-brand-2 bg-white shadow-lg">
            {isLoading && (
              <Combobox.Option
                disabled
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value=""
              >
                Загрузка...
              </Combobox.Option>
            )}
            {isSuccess && patients.length === 0 && (
              <Combobox.Option
                disabled
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value=""
              >
                Ничего не найдено
              </Combobox.Option>
            )}
            {patients.map((patient) => (
              <Combobox.Option
                key={patient.id}
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value={patient.id}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={classNames('block truncate', {
                        'font-bold': selected,
                      })}
                    >
                      {getPatientComboboxLabel(patient)}
                    </span>
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
};
